import type { RyftAccountGetResponseType } from "@sellar-io/api-v2/types";
import { Button, Card, CardContent } from "@sellar-io/components";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogNavigation,
} from "~/components/modal/Dialog";
import { BadgeAmber, BadgeRed } from "~/components/badge/Badge";
import {
  RyftEditDocumentForm,
  type RyftDocumentSubmissionValues,
} from "../components/RyftEditDocumentForm";
import {
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_TYPE_LABELS,
} from "../onboarding/constants";
import type {
  RyftRequiredDocument,
  RyftUploadedDocument,
} from "../onboarding/components/RyftNonHostedOnboardingProvider.shared";
import { useRyftNonHostedDashboardAccount } from "./components/RyftNonHostedDashboardAccountProvider.shared";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { useRyftManagementDocumentUpload } from "~/hooks/api/v2/ryftManagement/useRyftManagementDocumentUpload";
import { useRyftManagementDirectorDocumentUpload } from "~/hooks/api/v2/ryftManagement/useRyftManagementDirectorDocumentUpload";

type DocumentOwner = {
  type: "business" | "director";
  id: string;
  name: string;
  editable: boolean;
  verification: RyftAccountGetResponseType["verification"];
  documents: RyftUploadedDocument[];
};
type DocumentDialog = {
  ownerId: string;
  mode: "create" | "update";
  types: RyftRequiredDocument["types"];
};

export const RyftNonHostedDocuments = () => {
  const { account } = useRyftNonHostedDashboardAccount();
  const [dialog, setDialog] = useState<DocumentDialog>();
  const uploadBusinessDocumentMutation = useRyftManagementDocumentUpload();
  const uploadDirectorDocumentMutation =
    useRyftManagementDirectorDocumentUpload();

  const isSubmitting =
    uploadBusinessDocumentMutation.isPending ||
    uploadDirectorDocumentMutation.isPending;

  const canEdit = account.verification.status !== "PendingVerification";
  const owners: DocumentOwner[] = [
    {
      type: "business",
      id: account.ryftAccountId,
      name: "Business",
      editable: canEdit,
      verification: account.verification,
      documents: account.uploadedDocuments,
    },
    ...account.directors.map((director) => ({
      type: "director" as const,
      id: director.id,
      name: `${director.firstName} ${director.lastName}`,
      editable: director.status !== "PendingVerification",
      verification: director.verification,
      documents: director.documents ?? [],
    })),
  ];

  const owner = owners.find(({ id }) => id === dialog?.ownerId);

  const resetAndDismiss = () => {
    uploadBusinessDocumentMutation.reset();
    uploadDirectorDocumentMutation.reset();
    setDialog(undefined);
  };

  const dismiss = () => {
    if (isSubmitting) return;
    resetAndDismiss();
  };

  const uploadError =
    uploadBusinessDocumentMutation.error ??
    uploadDirectorDocumentMutation.error;

  const submissionError = getDocumentSubmissionError(uploadError);

  const submit = (data: RyftDocumentSubmissionValues) => {
    if (!owner?.editable || isSubmitting) {
      return;
    }

    if (owner.type === "director") {
      uploadDirectorDocumentMutation.mutate(
        { directorId: owner.id, ...data },
        { onSuccess: resetAndDismiss },
      );
      return;
    }

    uploadBusinessDocumentMutation.mutate(data, { onSuccess: resetAndDismiss });
  };

  const hasDocuments = owners.some(({ documents }) => documents.length > 0);

  const hasRequests = owners.some(
    ({ verification }) =>
      verification.requiredDocuments?.length ||
      verification.errors?.some((error) => error.code === "InvalidDocument"),
  );

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {hasRequests && (
        <section className="flex flex-col gap-3">
          <h2 className="font-semibold text-zinc-900">Document requests</h2>
          <Card className="border border-zinc-200 bg-white">
            <CardContent className="divide-y divide-zinc-200 p-0">
              {owners.map((item) => (
                <DocumentRequests
                  key={item.id}
                  owner={item}
                  onUpload={(types) =>
                    setDialog({ ownerId: item.id, mode: "create", types })
                  }
                />
              ))}
            </CardContent>
          </Card>
        </section>
      )}
      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-zinc-900">Submitted documents</h2>
        <Card className="border border-zinc-200 bg-white">
          <CardContent className="divide-y divide-zinc-200 p-0">
            {owners.flatMap((item) =>
              item.documents.map((document) => (
                <DocumentItem
                  key={`${item.id}-${document.front}`}
                  owner={item}
                  document={document}
                  onReplace={() =>
                    setDialog({
                      ownerId: item.id,
                      mode: "update",
                      types: [document.type],
                    })
                  }
                />
              )),
            )}
            {!hasDocuments && (
              <p className="p-5 text-sm text-zinc-600">
                No documents have been submitted yet.
              </p>
            )}
          </CardContent>
        </Card>
      </section>
      {!hasRequests && (
        <p className="text-sm text-zinc-500">
          No additional documents have been requested.
        </p>
      )}
      <Dialog active={Boolean(dialog)} onDismiss={dismiss}>
        <DialogNavigation
          title={
            dialog?.mode === "update" ? "Replace document" : "Upload document"
          }
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={dismiss}
        />
        <DialogContent className="flex flex-col gap-4">
          {dialog && owner && (
            <>
              <p className="text-sm text-zinc-600">
                {owner.name} · PDF, JPEG or PNG, between 10KB and 4MB.
              </p>
              {dialog.mode === "update" && (
                <p className="text-sm text-zinc-600">
                  Replacing this document may require Ryft to review it again.
                </p>
              )}
              {!owner.editable && (
                <p className="text-sm text-zinc-600">
                  Document submissions are unavailable during verification.
                </p>
              )}
              <RyftEditDocumentForm
                key={`${owner.id}-${dialog.mode}-${dialog.types.join(",")}`}
                mode={dialog.mode}
                documentTypes={dialog.types}
                isSubmitting={isSubmitting}
                submissionError={submissionError}
                disabled={!owner.editable}
                onSubmit={submit}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const getDocumentSubmissionError = (error: unknown): string | undefined => {
  if (!error) return undefined;
  return (
    apiV2AssertError(error)?.response?.data.message ??
    "Unable to upload document. Please try again."
  );
};

const DocumentRequests = ({
  owner,
  onUpload,
}: {
  owner: DocumentOwner;
  onUpload: (types: RyftRequiredDocument["types"]) => void;
}) => (
  <>
    {owner.verification.requiredDocuments?.map((request, index) => (
      <div
        key={`${request.category}-${index}`}
        className="flex items-start gap-4 p-4 md:p-5"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-zinc-900">
              {DOCUMENT_CATEGORY_LABELS[request.category]}
            </h3>
            <BadgeAmber>Upload required</BadgeAmber>
          </div>
          <p className="mt-1 text-sm text-zinc-600">
            {owner.name} · {request.quantity}{" "}
            {request.quantity === 1 ? "document" : "documents"}
          </p>
          <p className="mt-1 text-sm text-zinc-500">
            {request.types
              .map((type) => DOCUMENT_TYPE_LABELS[type])
              .join(" or ")}
          </p>
        </div>
        <Button
          color="secondary"
          size="small"
          disabled={!owner.editable || request.types.length === 0}
          onClick={() => onUpload(request.types)}
        >
          Upload
        </Button>
      </div>
    ))}
    {owner.verification.errors
      ?.filter((error) => error.code === "InvalidDocument")
      .map((error) => (
        <p key={`${owner.id}-${error.id}`} className="p-4 text-sm text-red-700">
          {error.description}
        </p>
      ))}
  </>
);

const DocumentStatusBadge = ({
  document,
}: {
  document: RyftUploadedDocument;
}) => {
  if (document.status === "Invalid" || document.invalidReason) {
    return <BadgeRed>Replacement required</BadgeRed>;
  }
  if (document.status === "Pending") {
    return <BadgeAmber>Pending verification</BadgeAmber>;
  }
  return null;
};

const DocumentItem = ({
  owner,
  document,
  onReplace,
}: {
  owner: DocumentOwner;
  document: RyftUploadedDocument;
  onReplace: () => void;
}) => (
  <div className="flex flex-col gap-3 p-4 md:p-5">
    <div className="flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-zinc-900">
          {DOCUMENT_TYPE_LABELS[document.type]}
        </h3>
        <p className="mt-1 text-sm text-zinc-600">
          {owner.name} · {DOCUMENT_CATEGORY_LABELS[document.category]}
        </p>
      </div>
      <DocumentStatusBadge document={document} />
      {owner.editable && (
        <Button color="slim" size="small" onClick={onReplace}>
          Replace
        </Button>
      )}
    </div>
    {document.invalidReason && (
      <p className="border-t border-zinc-200 pt-3 text-sm text-red-700">
        {document.invalidReason}
      </p>
    )}
  </div>
);
